import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { VoiceRecorder } from "capacitor-voice-recorder";

type NativeRecorderError = {
  code?: string;
  message?: string;
};

const base64ToFile = (base64: string, mimeType: string) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  const extension = mimeType.includes("aac")
    ? "aac"
    : mimeType.includes("mp4")
      ? "m4a"
      : mimeType.includes("ogg")
        ? "ogg"
        : mimeType.includes("wav")
          ? "wav"
          : "webm";

  return new File([bytes], `voice-message.${extension}`, { type: mimeType });
};

export const isNativeVoiceRecorderPlatform = () => Capacitor.isNativePlatform();

export const getNativeRecorderErrorMessage = (error: unknown) => {
  const nativeError = (error ?? {}) as NativeRecorderError;

  switch (nativeError.code) {
    case "MISSING_PERMISSION":
      return "Microphone permission is required to record voice.";
    case "DEVICE_CANNOT_VOICE_RECORD":
      return "This device cannot record voice.";
    case "MICROPHONE_BEING_USED":
      return "The microphone is being used by another app.";
    case "ALREADY_RECORDING":
      return "Voice recording is already in progress.";
    case "FAILED_TO_RECORD":
      return "Failed to start voice recording on this device.";
    case "EMPTY_RECORDING":
      return "Recording was too short. Please try again.";
    case "FAILED_TO_FETCH_RECORDING":
      return "Failed to fetch the recorded audio.";
    case "RECORDING_HAS_NOT_STARTED":
      return "Recording has not started yet.";
    default:
      return nativeError.message || "Unable to record voice on this device.";
  }
};

export const startNativeVoiceRecording = async () => {
  const capability = await VoiceRecorder.canDeviceVoiceRecord();
  if (!capability.value) {
    throw new Error("This device cannot record voice.");
  }

  const permission = await VoiceRecorder.hasAudioRecordingPermission();
  if (!permission.value) {
    const requested = await VoiceRecorder.requestAudioRecordingPermission();
    if (!requested.value) {
      throw { code: "MISSING_PERMISSION" } satisfies NativeRecorderError;
    }
  }

  await VoiceRecorder.startRecording({
    directory: Directory.Cache,
    subDirectory: "voice-recordings",
  });
};

export const stopNativeVoiceRecording = async () => {
  const result = await VoiceRecorder.stopRecording();
  const { recordDataBase64, mimeType, path } = result.value;

  if (recordDataBase64 && mimeType) {
    return base64ToFile(recordDataBase64, mimeType);
  }

  if (!path || !mimeType) {
    throw { code: "FAILED_TO_FETCH_RECORDING" } satisfies NativeRecorderError;
  }

  const fileResult = await Filesystem.readFile({
    path,
    directory: Directory.Cache,
  });

  if (!fileResult.data || typeof fileResult.data !== "string") {
    throw { code: "FAILED_TO_FETCH_RECORDING" } satisfies NativeRecorderError;
  }

  return base64ToFile(fileResult.data, mimeType);
};
